import React from 'react';

export interface DefaultPreviewField {
  /** Column name to populate with a constant default value (e.g. "status", "state"). */
  field: string;
  /** Default value to show in the Preview grid for that column (e.g. "New", "Open"). */
  value: string;
}

/** Surfaces one of several possible mapped source fields under a single alias column (e.g. "asset_tag"/"serial_number" -> "asset"). */
export interface AliasFieldConfig {
  sourceFields: string[];
  targetField: string;
}

/** Fields that should fall back to `placeholder` instead of an empty string when the CSV cell is empty/unmapped. */
export interface FallbackFieldConfig {
  fields: Set<string>;
  placeholder: string;
}

/** Shared alias config used by the Finding and Work Order CSV import wizards to surface the
 *  picked Asset identifier column (asset_tag or serial_number) under an "asset" column. */
export const ASSET_ALIAS_FIELD: AliasFieldConfig = {
  sourceFields: ['asset_tag', 'serial_number'],
  targetField: 'asset',
};

/**
 * Builds the Preview-step data source shared by the Asset, Finding, and Work Order CSV import
 * wizards: maps each CSV row to an object keyed by field name (using the CSV-header -> field
 * `mapping`), fills in a constant default field (Status/State), optionally surfaces a picked
 * source column under an alias column (e.g. asset_tag/serial_number -> "asset"), optionally
 * falls back to a placeholder value for designated fields when the CSV cell is empty, and fills
 * any remaining unmapped columns with an empty string (or the fallback placeholder).
 */
export function useCsvPreviewDataSource(
  mapping: Record<string, string>,
  csvTopLines: string[][],
  columnsFields: string[],
  defaultField: DefaultPreviewField,
  aliasField?: AliasFieldConfig,
  fallback?: FallbackFieldConfig,
): Record<string, string>[] {
  return React.useMemo(() => {
    if (csvTopLines.length === 0) return [];

    const [headerRow, ...dataRows] = csvTopLines;

    // Create a reverse mapping: CSV column index -> field name
    const columnIndexToField: Record<number, string> = {};
    Object.entries(mapping).forEach(([fieldName, csvHeader]) => {
      const columnIndex = headerRow.indexOf(csvHeader);
      if (columnIndex !== -1) {
        columnIndexToField[columnIndex] = fieldName;
      }
    });

    // Transform each data row into an object using the mapping
    return dataRows.map((row) => {
      const rowData: Record<string, string> = {};
      row.forEach((cellValue, columnIndex) => {
        const fieldName = columnIndexToField[columnIndex];
        const usesFallback = !!fieldName && fallback?.fields.has(fieldName);

        if (fieldName) {
          rowData[fieldName] = usesFallback && !cellValue ? fallback!.placeholder : cellValue;
        }

        // Include the default Status/State field
        rowData[defaultField.field] = defaultField.value;

        // Surface the picked source column (e.g. asset_tag or serial_number) under the alias field
        if (fieldName && aliasField?.sourceFields.includes(fieldName) && mapping[fieldName]) {
          rowData[aliasField.targetField] = cellValue;
        }

        // Fill unmapped columns with an empty string (or the fallback placeholder, if configured)
        for (const field of columnsFields) {
          if (!(field in rowData)) {
            rowData[field] = fallback?.fields.has(field) ? fallback.placeholder : '';
          }
        }
      });
      return rowData;
    });
  }, [
    mapping,
    csvTopLines,
    columnsFields,
    defaultField.field,
    defaultField.value,
    aliasField,
    fallback,
  ]);
}
